import { useState, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { CustomerPaymentSheet } from "@/components/customer-payment-sheet";
import { ArrowUpDown, ArrowUp, ArrowDown, Search, X, Printer, FileSpreadsheet, ChevronUp, ChevronDown, AlertTriangle, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

interface OverdueTableProps {
  customers: Customer[];
  products: Product[];
  onAddProduct: (product: Omit<Product, "id" | "createdAt" | "remainingAmount">) => void;
  onDeleteProduct: (id: string) => void;
  onUpdateProduct: (product: Product) => void;
  onNavigate?: (page: "dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports" | "settings") => void;
}

export function OverdueTable({ customers, products, onAddProduct, onDeleteProduct, onUpdateProduct, onNavigate }: OverdueTableProps) {
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [jobFilter, setJobFilter] = useState<string | null>(null);
  const [currencyFilter, setCurrencyFilter] = useState<"all" | "IQD" | "USD">("all");
  const [daysFilter, setDaysFilter] = useState<"all" | "week" | "month" | "more">("all");
  const [jobPopoverOpen, setJobPopoverOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const printRef = useRef<HTMLDivElement>(null);

  const toggleExpanded = (productId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set<string>();
      if (!prev.has(productId)) {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: "دواکەوتووەکان",
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 8mm;
      }
      @media print {
        * {
          box-sizing: border-box;
        }
        html {
          transform: scale(0.7);
          transform-origin: top right;
        }
        body {
          direction: rtl;
          font-family: 'Noto Kufi Arabic', sans-serif;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          margin: 0;
          padding: 0;
        }
        .print-table {
          width: 142% !important;
          table-layout: auto;
          border-collapse: collapse;
        }
        .print-table th, .print-table td {
          border: 1px solid #ddd;
          padding: 6px 8px;
          text-align: right;
          white-space: nowrap;
        }
        .print-table th {
          background-color: #f5f5f5 !important;
          font-weight: bold;
        }
      }
    `,
  });

  const exportToExcel = () => {
    const data = sortedOverdue.map(item => ({
      "کۆد": item.customer?.customerCode || "",
      "ناوی کڕیار": item.customer?.fullName || "",
      "پیشە": item.customer?.job || "",
      "ناوی ئامێر": item.product.deviceName,
      "بڕی ماوە": item.product.remainingAmount,
      "دراو": item.product.currency === "IQD" ? "دینار" : "دۆلار",
      "کڕین": new Date(item.product.date).toLocaleDateString("ar-IQ"),
      "جار": item.product.installments || 0,
      "شایستە": (item.product.installments || 0) === 0 ? "پارەدان نەکراوە" : item.dueDate.toLocaleDateString("ar-IQ"),
      "ماوەی دواکەوتن": item.daysOverdue,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "دواکەوتووەکان");
    
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `دواکەوتووەکان-${new Date().toLocaleDateString("ar-IQ")}.xlsx`);
  };

  const handleViewCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setProfileOpen(true);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortField(null);
        setSortDirection("asc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
    }
    return sortDirection === "asc" 
      ? <ArrowUp className="h-3 w-3 text-primary" />
      : <ArrowDown className="h-3 w-3 text-primary" />;
  };

  // Calculate due date for each product
  // ئەگەر هیچ پارەدانێک نەکرابوو: بەرواری شایستە = بەرواری کڕین + 30 ڕۆژ
  // ئەگەر پارەدان کرابوو: بەرواری شایستە = بەرواری کڕین + (ژمارەی قیستەکان + 1) × 30 ڕۆژ
  const getNextDueDate = (product: Product): Date => {
    const purchaseDate = new Date(product.date);
    const installmentsPaid = product.installments || 0;
    const nextDueDate = new Date(purchaseDate);
    
    if (installmentsPaid === 0) {
      // هیچ پارەدانێک نەکراوە - بەرواری شایستە = بەرواری کڕین + 30 ڕۆژ
      nextDueDate.setDate(nextDueDate.getDate() + 30);
    } else {
      // پارەدان کراوە - بەرواری قیستی داهاتوو
      nextDueDate.setDate(nextDueDate.getDate() + ((installmentsPaid + 1) * 30));
    }
    
    return nextDueDate;
  };

  const getDaysOverdue = (dueDate: Date): number => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = today.getTime() - due.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get overdue products with customer info
  const overdueProducts = products
    .filter(p => p.remainingAmount > 0)
    .map(product => {
      const customer = customers.find(c => c.id === product.customerId);
      const dueDate = getNextDueDate(product);
      const daysOverdue = getDaysOverdue(dueDate);
      return {
        product,
        customer,
        dueDate,
        daysOverdue,
      };
    })
    .filter(item => item.daysOverdue > 0 && item.customer);

  // Get unique jobs for filter
  const uniqueJobs = [...new Set(overdueProducts.map(item => item.customer?.job).filter(Boolean))] as string[];

  // Apply filters
  const filteredOverdue = overdueProducts.filter(item => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        item.customer?.fullName.toLowerCase().includes(search) ||
        item.customer?.customerCode.toString().includes(search) ||
        item.customer?.phone1.includes(search) ||
        item.product.deviceName.toLowerCase().includes(search) ||
        item.customer?.job?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    // Job filter
    if (jobFilter && item.customer?.job !== jobFilter) return false;

    // Currency filter
    if (currencyFilter !== "all" && item.product.currency !== currencyFilter) return false;

    // Days overdue filter
    if (daysFilter === "week" && item.daysOverdue > 7) return false;
    if (daysFilter === "month" && (item.daysOverdue <= 7 || item.daysOverdue > 30)) return false;
    if (daysFilter === "more" && item.daysOverdue <= 30) return false;

    return true;
  });

  // Sort overdue products
  const sortedOverdue = [...filteredOverdue].sort((a, b) => {
    if (!sortField) return b.daysOverdue - a.daysOverdue; // Default: most overdue first
    
    let aValue: string | number = "";
    let bValue: string | number = "";
    
    switch (sortField) {
      case "code":
        aValue = a.customer?.customerCode || 0;
        bValue = b.customer?.customerCode || 0;
        break;
      case "name":
        aValue = a.customer?.fullName || "";
        bValue = b.customer?.fullName || "";
        break;
      case "job":
        aValue = a.customer?.job || "";
        bValue = b.customer?.job || "";
        break;
      case "device":
        aValue = a.product.deviceName;
        bValue = b.product.deviceName;
        break;
      case "remaining":
        aValue = a.product.remainingAmount;
        bValue = b.product.remainingAmount;
        break;
      case "dueDate":
        aValue = a.dueDate.getTime();
        bValue = b.dueDate.getTime();
        break;
      case "daysOverdue":
        aValue = a.daysOverdue;
        bValue = b.daysOverdue;
        break;
      case "purchaseDate":
        aValue = new Date(a.product.date).getTime();
        bValue = new Date(b.product.date).getTime();
        break;
      case "installments":
        aValue = a.product.installments || 0;
        bValue = b.product.installments || 0;
        break;
    }
    
    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
    }
    
    return sortDirection === "asc" 
      ? String(aValue).localeCompare(String(bValue), "ar")
      : String(bValue).localeCompare(String(aValue), "ar");
  });

  if (overdueProducts.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        هیچ دواکەوتنێک نییە
      </div>
    );
  }

  const hasActiveFilters = searchTerm || jobFilter || currencyFilter !== "all" || daysFilter !== "all";

  const clearFilters = () => {
    setSearchTerm("");
    setJobFilter(null);
    setCurrencyFilter("all");
    setDaysFilter("all");
  };

  return (
    <>
      {/* Desktop Search and Actions Bar */}
      <div className="hidden md:flex items-center justify-between gap-4 mb-4">
        <div className="relative w-72">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="گەڕان..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pr-9"
          />
        </div>
        
        <div className="flex items-center gap-2">
          {/* Job Filter */}
          <Popover open={jobPopoverOpen} onOpenChange={setJobPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant={jobFilter ? "default" : "outline"} size="sm">
                {jobFilter || "پیشە"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-1" align="start" dir="rtl">
              <div
                className={cn(
                  "px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent",
                  !jobFilter && "bg-accent"
                )}
                onClick={() => { setJobFilter(null); setJobPopoverOpen(false); }}
              >
                هەموو
              </div>
              {uniqueJobs.map((job) => (
                <div
                  key={job}
                  className={cn(
                    "px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent",
                    jobFilter === job && "bg-accent"
                  )}
                  onClick={() => { setJobFilter(job); setJobPopoverOpen(false); }}
                >
                  {job}
                </div>
              ))}
            </PopoverContent>
          </Popover>

          {/* Currency Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant={currencyFilter !== "all" ? "default" : "outline"} size="sm">
                {currencyFilter === "all" ? "دراو" : currencyFilter === "IQD" ? "دینار" : "دۆلار"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-32 p-1" align="start" dir="rtl">
              <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", currencyFilter === "all" && "bg-accent")} onClick={() => setCurrencyFilter("all")}>هەموو</div>
              <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", currencyFilter === "IQD" && "bg-accent")} onClick={() => setCurrencyFilter("IQD")}>دینار</div>
              <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", currencyFilter === "USD" && "bg-accent")} onClick={() => setCurrencyFilter("USD")}>دۆلار</div>
            </PopoverContent>
          </Popover>

          {/* Days Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant={daysFilter !== "all" ? "default" : "outline"} size="sm">
                {daysFilter === "all" ? "ماوە" : daysFilter === "week" ? "١-٧ ڕۆژ" : daysFilter === "month" ? "٨-٣٠ ڕۆژ" : "+٣٠ ڕۆژ"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-32 p-1" align="start" dir="rtl">
              <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "all" && "bg-accent")} onClick={() => setDaysFilter("all")}>هەموو</div>
              <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "week" && "bg-accent")} onClick={() => setDaysFilter("week")}>١-٧ ڕۆژ</div>
              <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "month" && "bg-accent")} onClick={() => setDaysFilter("month")}>٨-٣٠ ڕۆژ</div>
              <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "more" && "bg-accent")} onClick={() => setDaysFilter("more")}>+٣٠ ڕۆژ</div>
            </PopoverContent>
          </Popover>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4" />
            </Button>
          )}

          <div className="h-6 w-px bg-border mx-1" />

          <Button variant="outline" size="sm" onClick={() => handlePrint()}>
            <Printer className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={exportToExcel}>
            <FileSpreadsheet className="h-4 w-4" />
          </Button>

          <Badge variant="secondary" className="mr-2">{sortedOverdue.length}</Badge>
        </div>
      </div>

      {/* Mobile Search Bar */}
      <div className="md:hidden mb-4 space-y-3">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="گەڕان..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pr-9"
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Job Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant={jobFilter ? "default" : "outline"} size="sm">
                  {jobFilter || "پیشە"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-1" align="start" dir="rtl">
                <div
                  className={cn(
                    "px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent",
                    !jobFilter && "bg-accent"
                  )}
                  onClick={() => setJobFilter(null)}
                >
                  هەموو
                </div>
                {uniqueJobs.map((job) => (
                  <div
                    key={job}
                    className={cn(
                      "px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent",
                      jobFilter === job && "bg-accent"
                    )}
                    onClick={() => setJobFilter(job)}
                  >
                    {job}
                  </div>
                ))}
              </PopoverContent>
            </Popover>

            {/* Currency Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant={currencyFilter !== "all" ? "default" : "outline"} size="sm">
                  {currencyFilter === "all" ? "دراو" : currencyFilter === "IQD" ? "دینار" : "دۆلار"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-32 p-1" align="start" dir="rtl">
                <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", currencyFilter === "all" && "bg-accent")} onClick={() => setCurrencyFilter("all")}>هەموو</div>
                <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", currencyFilter === "IQD" && "bg-accent")} onClick={() => setCurrencyFilter("IQD")}>دینار</div>
                <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", currencyFilter === "USD" && "bg-accent")} onClick={() => setCurrencyFilter("USD")}>دۆلار</div>
              </PopoverContent>
            </Popover>

            {/* Days Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant={daysFilter !== "all" ? "default" : "outline"} size="sm">
                  {daysFilter === "all" ? "ماوە" : daysFilter === "week" ? "١-٧ ڕۆژ" : daysFilter === "month" ? "٨-٣٠ ڕۆژ" : "+٣٠ ڕۆژ"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-32 p-1" align="start" dir="rtl">
                <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "all" && "bg-accent")} onClick={() => setDaysFilter("all")}>هەموو</div>
                <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "week" && "bg-accent")} onClick={() => setDaysFilter("week")}>١-٧ ڕۆژ</div>
                <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "month" && "bg-accent")} onClick={() => setDaysFilter("month")}>٨-٣٠ ڕۆژ</div>
                <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "more" && "bg-accent")} onClick={() => setDaysFilter("more")}>+٣٠ ڕۆژ</div>
              </PopoverContent>
            </Popover>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => handlePrint()}>
              <Printer className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={exportToExcel}>
              <FileSpreadsheet className="h-4 w-4" />
            </Button>
            <Badge variant="secondary">{sortedOverdue.length}</Badge>
          </div>
        </div>
      </div>

      {sortedOverdue.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          هیچ ئەنجامێک نەدۆزرایەوە
        </div>
      ) : (
      <>
      {/* Desktop Table */}
      <div ref={printRef} className="rounded-md border print:border-0 hidden md:block">
        {/* Print Header - تەنها لە کاتی چاپکردن دەردەکەوێت */}
        <div className="hidden print:flex print:flex-col print:items-center print:justify-center mb-4 text-center border-b pb-3">
          <h1 className="text-xl font-bold mb-1">لیستی دواکەوتووەکان</h1>
          <p className="text-sm text-muted-foreground">
            بەرواری چاپکردن: {new Date().toLocaleDateString("ku", { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <Table className="print-table">
          <TableHeader>
            <TableRow>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "code" ? "bg-muted/30" : ""}`} onClick={() => handleSort("code")}>
                <div className="flex items-center gap-1">
                  کۆد
                  <SortIcon field="code" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "name" ? "bg-muted/30" : ""}`} onClick={() => handleSort("name")}>
                <div className="flex items-center gap-1">
                  ناوی کڕیار
                  <SortIcon field="name" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "job" ? "bg-muted/30" : ""}`} onClick={() => handleSort("job")}>
                <div className="flex items-center gap-1">
                  پیشە
                  <SortIcon field="job" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "device" ? "bg-muted/30" : ""}`} onClick={() => handleSort("device")}>
                <div className="flex items-center gap-1">
                  ناوی ئامێر
                  <SortIcon field="device" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "remaining" ? "bg-muted/30" : ""}`} onClick={() => handleSort("remaining")}>
                <div className="flex items-center gap-1">
                  بڕی ماوە
                  <SortIcon field="remaining" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "purchaseDate" ? "bg-muted/30" : ""}`} onClick={() => handleSort("purchaseDate")}>
                <div className="flex items-center gap-1">
                  کڕین
                  <SortIcon field="purchaseDate" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "installments" ? "bg-muted/30" : ""}`} onClick={() => handleSort("installments")}>
                <div className="flex items-center gap-1">
                  جار
                  <SortIcon field="installments" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "dueDate" ? "bg-muted/30" : ""}`} onClick={() => handleSort("dueDate")}>
                <div className="flex items-center gap-1">
                  شایستە
                  <SortIcon field="dueDate" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "daysOverdue" ? "bg-muted/30" : ""}`} onClick={() => handleSort("daysOverdue")}>
                <div className="flex items-center gap-1">
                  ماوەی دواکەوتن
                  <SortIcon field="daysOverdue" />
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedOverdue.map((item, index) => (
              <TableRow 
                key={item.product.id} 
                className={`h-14 ${index % 2 === 1 ? 'bg-muted/50' : ''}`}
              >
                <TableCell className="py-2">
                  <Badge variant="outline">{item.customer?.customerCode}</Badge>
                </TableCell>
                <TableCell className="font-medium py-2">{item.customer?.fullName}</TableCell>
                <TableCell className="py-2">
                  {item.customer?.job ? <Badge variant="secondary">{item.customer.job}</Badge> : "-"}
                </TableCell>
                <TableCell className="py-2">{item.product.deviceName}</TableCell>
                <TableCell className="py-2" dir="ltr">
                  <div className="text-right">
                    {item.product.remainingAmount.toLocaleString()}{" "}
                    <Badge variant="destructive" className="mr-1">
                      {item.product.currency === "IQD" ? "د.ع" : "$"}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell className="py-2">
                  <span dir="ltr">{new Date(item.product.date).toLocaleDateString("ar-IQ")}</span>
                </TableCell>
                <TableCell className="py-2">
                  <Badge variant="secondary">{item.product.installments || 0}</Badge>
                </TableCell>
                <TableCell className="py-2">
                  {(item.product.installments || 0) === 0 ? (
                    <Badge variant="outline" className="text-destructive border-destructive">پارەدان نەکراوە</Badge>
                  ) : (
                    <span dir="ltr">{item.dueDate.toLocaleDateString("ar-IQ")}</span>
                  )}
                </TableCell>
                <TableCell className="py-2">
                  <Badge variant={item.daysOverdue > 30 ? "destructive" : "secondary"}>
                    {item.daysOverdue} ڕۆژ
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {sortedOverdue.map((item, index) => {
          const isExpanded = expandedItems.has(item.product.id);
          const isEven = index % 2 === 1;
          return (
            <div 
              key={item.product.id} 
              className={`rounded-xl border overflow-hidden transition-all duration-200 ${isExpanded ? 'border-primary ring-1 ring-primary/20' : ''} ${isEven ? 'bg-muted/60' : 'bg-card'}`}
            >
              <div className="p-3 cursor-pointer" onClick={() => toggleExpanded(item.product.id)}>
                {/* Main Row */}
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm truncate">{item.customer?.fullName}</span>
                      <Badge variant="outline" className="text-[10px] px-1.5 h-4">
                        {item.customer?.customerCode}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {item.product.deviceName}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant={item.daysOverdue > 30 ? "destructive" : "secondary"} className="text-[10px]">
                      {item.daysOverdue} ڕۆژ
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(item.product.id);
                      }}
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
                {/* Mini Table */}
                <div className="grid grid-cols-3 gap-1 mt-2 text-xs bg-muted/30 rounded-lg p-2 w-full" dir="rtl">
                  <div className="text-center">
                    <div className="text-muted-foreground text-[10px]">ماوە</div>
                    <div className="font-semibold text-destructive" dir={item.product.currency === "IQD" ? "rtl" : "ltr"}>
                      {item.product.currency === "IQD" ? (
                        <>{item.product.remainingAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></>
                      ) : (
                        <><span className="text-[10px]">$ </span>{item.product.remainingAmount.toLocaleString()}</>
                      )}
                    </div>
                  </div>
                  <div className="text-center border-x border-dashed">
                    <div className="text-muted-foreground text-[10px]">قیست</div>
                    <div className="font-semibold">{item.product.installments || 0}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-muted-foreground text-[10px]">شایستە</div>
                    <div className="font-semibold text-[10px]">
                      {(item.product.installments || 0) === 0 ? (
                        <span className="text-destructive">نەکراوە</span>
                      ) : (
                        <span dir="ltr">{item.dueDate.toLocaleDateString("ar-IQ")}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {isExpanded && (
                <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                  <div className="space-y-2 pt-3">
                    {item.customer?.phone1 && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">مۆبایل</span>
                        <span className="font-medium text-sm flex items-center gap-1" dir="ltr">
                          <Phone className="h-3 w-3" />
                          {item.customer.phone1}
                        </span>
                      </div>
                    )}
                    {item.customer?.job && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">پیشە</span>
                        <Badge variant="secondary" className="text-xs">{item.customer.job}</Badge>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                      <span className="text-muted-foreground text-xs">بەرواری کڕین</span>
                      <span className="font-medium text-sm" dir="ltr">{new Date(item.product.date).toLocaleDateString("ar-IQ")}</span>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                      <span className="text-muted-foreground text-xs">نرخی فرۆشتن</span>
                      <span className="font-medium text-sm" dir={item.product.currency === "IQD" ? "rtl" : "ltr"}>
                        {item.product.currency === "IQD" ? (
                          <>{item.product.salePrice.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></>
                        ) : (
                          <><span className="text-[10px] text-muted-foreground">$ </span>{item.product.salePrice.toLocaleString()}</>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1.5">
                      <span className="text-muted-foreground text-xs">قیستی مانگانە</span>
                      <span className="font-medium text-sm" dir={item.product.currency === "IQD" ? "rtl" : "ltr"}>
                        {item.product.currency === "IQD" ? (
                          <>{item.product.monthlyInstallment.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></>
                        ) : (
                          <><span className="text-[10px] text-muted-foreground">$ </span>{item.product.monthlyInstallment.toLocaleString()}</>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>
      )}

      <CustomerPaymentSheet
        customer={selectedCustomer}
        products={products}
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onAddProduct={onAddProduct}
        onDeleteProduct={onDeleteProduct}
        onUpdateProduct={onUpdateProduct}
        onNavigate={onNavigate}
        hideAddProduct={true}
      />
    </>
  );
}
